<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TemplateCard from 'dashboard/components-next/Campaigns/Advanced/Templates/TemplateCard.vue';
import TemplateFormDialog from 'dashboard/components-next/Campaigns/Advanced/Templates/TemplateFormDialog.vue';

import CampaignTemplatesAPI from 'dashboard/api/campaignTemplates';

const { t } = useI18n();

const templates = ref([]);
const categories = ref([]);
const isLoading = ref(false);
const searchQuery = ref('');
const selectedCategory = ref('');

// Dialog refs
const formDialogRef = ref(null);
const editingTemplate = ref(null);

const filteredTemplates = computed(() => {
  let result = templates.value;

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(
      template =>
        template.name.toLowerCase().includes(query) ||
        template.content?.toLowerCase().includes(query)
    );
  }

  if (selectedCategory.value) {
    result = result.filter(
      template => template.category === selectedCategory.value
    );
  }

  return result;
});

const categoryOptions = computed(() => [
  { value: '', label: 'Todas as categorias' },
  ...categories.value.map(c => ({ value: c, label: c })),
]);

const fetchTemplates = async () => {
  isLoading.value = true;
  try {
    const [templatesRes, categoriesRes] = await Promise.all([
      CampaignTemplatesAPI.get(),
      CampaignTemplatesAPI.getCategories(),
    ]);
    templates.value = templatesRes.data || [];
    categories.value = categoriesRes.data || [];
  } catch (error) {
    useAlert(t('CAMPAIGN.TEMPLATES.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openCreateDialog = () => {
  editingTemplate.value = null;
  formDialogRef.value?.open();
};

const openEditDialog = template => {
  editingTemplate.value = template;
  formDialogRef.value?.open();
};

const handleDialogClose = () => {
  editingTemplate.value = null;
};

const handleSaveTemplate = async payload => {
  try {
    if (payload.id) {
      await CampaignTemplatesAPI.update(payload.id, payload);
      useAlert(t('CAMPAIGN.TEMPLATES.UPDATE_SUCCESS'));
    } else {
      await CampaignTemplatesAPI.create(payload);
      useAlert(t('CAMPAIGN.TEMPLATES.CREATE_SUCCESS'));
    }
    fetchTemplates();
  } catch (error) {
    useAlert(
      payload.id
        ? t('CAMPAIGN.TEMPLATES.UPDATE_ERROR')
        : t('CAMPAIGN.TEMPLATES.CREATE_ERROR')
    );
  }
};

const handleUse = template => {
  // TODO: Navigate to campaign creation with template pre-selected
  useAlert(`Template "${template.name}" selecionado para uso`);
};

const handleDuplicate = async template => {
  try {
    await CampaignTemplatesAPI.duplicate(template.id);
    useAlert(t('CAMPAIGN.TEMPLATES.DUPLICATE_SUCCESS'));
    fetchTemplates();
  } catch (error) {
    useAlert(t('CAMPAIGN.TEMPLATES.DUPLICATE_ERROR'));
  }
};

const handleDelete = async template => {
  if (!confirm(t('CAMPAIGN.TEMPLATES.DELETE_CONFIRM'))) return;

  try {
    await CampaignTemplatesAPI.delete(template.id);
    useAlert(t('CAMPAIGN.TEMPLATES.DELETE_SUCCESS'));
    fetchTemplates();
  } catch (error) {
    useAlert(t('CAMPAIGN.TEMPLATES.DELETE_ERROR'));
  }
};

const handlePreview = async template => {
  try {
    const response = await CampaignTemplatesAPI.preview(template.id);
    alert(response.data.content);
  } catch (error) {
    useAlert(t('CAMPAIGN.TEMPLATES.PREVIEW_ERROR'));
  }
};

onMounted(() => {
  fetchTemplates();
});
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.TEMPLATES.TITLE')"
    :button-label="t('CAMPAIGN.TEMPLATES.CREATE')"
    @click="openCreateDialog"
  >
    <div class="space-y-4">
      <div class="flex gap-4">
        <Input
          v-model="searchQuery"
          class="flex-1"
          placeholder="Buscar templates..."
          icon="i-lucide-search"
        />
        <div class="w-48">
          <ComboBox
            v-model="selectedCategory"
            :options="categoryOptions"
            placeholder="Categoria"
            class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak"
          />
        </div>
      </div>

      <Spinner v-if="isLoading" />

      <div v-else class="space-y-4">
        <TemplateCard
          v-for="template in filteredTemplates"
          :key="template.id"
          :template="template"
          @use="handleUse"
          @edit="openEditDialog"
          @duplicate="handleDuplicate"
          @delete="handleDelete"
          @preview="handlePreview"
        />

        <div v-if="filteredTemplates.length === 0" class="py-12 text-center">
          <div class="i-lucide-file-text mx-auto size-12 text-n-slate-8" />
          <h3 class="mt-4 text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.TEMPLATES.EMPTY') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11">
            Crie seu primeiro template para agilizar suas campanhas.
          </p>
          <Button class="mt-4" icon="i-lucide-plus" @click="openCreateDialog">
            Criar template
          </Button>
        </div>
      </div>
    </div>

    <!-- Create/Edit Dialog -->
    <TemplateFormDialog
      ref="formDialogRef"
      :template="editingTemplate"
      :categories="categories"
      @close="handleDialogClose"
      @save="handleSaveTemplate"
    />
  </CampaignLayout>
</template>
